import type { ModalIbanAccountStep, QuizStepProps } from "@/features/user/quiz/types.ts";
import { useTranslation } from "react-i18next";
import { useQuiz } from "@/features/user/quiz/hooks/use-quiz.ts";
import { Base, SectionTitleSmall } from "@/atoms-react/text/Text.react.tsx";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react.tsx";
import InputLabel from "@/atoms-react/input/Input.react.tsx";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react.tsx";
import { Button } from "@/atoms-react/button/Button.react.tsx";
import { Themes } from "@/types/theme.ts";
import Loader from "@/atoms-react/loader/Loader.react.tsx";
import { usei18n } from "@/features/utils/hooks/use-i18n";

export const PaymentUsageStep = ({
  setStep,
  quizResponses,
  setQuizResponses,
  stepSettings,
  setPrevStep,
  totalSteps = 8
}: QuizStepProps) => {
  const { t } = useTranslation(["pass"]);
  const { getLanguage } = usei18n();
  const { PaymentsUsageQuery } = useQuiz();

  const { data: payments_usage, isLoading: isLoadingPaymentsUsage } = PaymentsUsageQuery(
    getLanguage()
  );
  const codeOfOtherPayment = payments_usage?.find((item) => item.name === "Autre")?.id;
  const handleCheckboxChange = (id: string, isChecked: boolean) =>
    setQuizResponses?.((prev) => {
      const newSourcesIncome = isChecked
        ? [...prev.payments_usage, { id, comment: "" }]
        : prev.payments_usage.filter((income) => Number(income.id) !== Number(id));

      return {
        ...prev,
        payments_usage: newSourcesIncome
      };
    });

  const handleCommentChange = (id: number, otherInputIncomes: string) =>
    setQuizResponses?.((prev) => {
      const updatedSourcesIncome = prev.payments_usage.map((income) =>
        Number(id) === Number(income.id) ? { ...income, comment: otherInputIncomes } : income
      );
      return {
        ...prev,
        payments_usage: updatedSourcesIncome
      };
    });

  const isQuizAnswered = () => {
    if (!quizResponses?.payments_usage?.length) return false;
    const otherIncomes = quizResponses?.payments_usage?.find(
      (income) => Number(income.id) === Number(codeOfOtherPayment)
    );
    if (otherIncomes && (otherIncomes?.comment as string).trim() === "") return false;

    return true;
  };

  return (
    <>
      <SectionTitleSmall>
        {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.payment_usage.question")}
      </SectionTitleSmall>
      {isLoadingPaymentsUsage && (
        <div className="mt-4 mb-4 flex flex-1 items-center justify-center">
          <Loader className="text-gray-500" />
        </div>
      )}
      {!isLoadingPaymentsUsage && (
        <>
          <div className="my-4 mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
            {payments_usage &&
              payments_usage.map((c , index) => (
                <Checkbox
                  key={c.id}
                  dataCy={`payment-usage-${index}`}
                  value={
                    quizResponses?.payments_usage &&
                    quizResponses.payments_usage.some(
                      (income) => Number(income.id) === Number(c.id)
                    )
                  }
                  onChange={(value) => handleCheckboxChange(c.id, value)}
                >
                  <Base className="mb-1 cursor-pointer">
                    {getLanguage() === "fr" ? c.name : c.english_name}
                  </Base>
                </Checkbox>
              ))}
          </div>
          {quizResponses?.payments_usage &&
            quizResponses.payments_usage.some(
              (income) => Number(income.id) === Number(codeOfOtherPayment)
            ) && (
              <div className={"mb-8"}>
                <InputLabel
                  label={t(
                    "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.payment_usage.responses.others_comment"
                  )}
                  value={
                    quizResponses.payments_usage.find(
                      (income) => Number(income.id) === Number(codeOfOtherPayment)
                    )?.comment as string
                  }
                  onChange={(value: string) =>
                    handleCommentChange(Number(codeOfOtherPayment), value)
                  }
                  className="mb-1 flex-1 md:mb-0"
                />
              </div>
            )}
          <div className="mt-auto mb-4 flex flex-col items-center">
            <DotsNavigation total={totalSteps} current={stepSettings.current} className="mb-4" />
            <div className="flex w-full flex-col-reverse gap-y-2 md:flex-row md:gap-x-2">
              <Button
                theme={Themes.white}
                onClick={() => setPrevStep(stepSettings.previous as ModalIbanAccountStep)}
              >
                {t("olkypass.general.go_back")}
              </Button>

              <Button
                disabled={!isQuizAnswered()}
                onClick={() => setStep(stepSettings.next as ModalIbanAccountStep)}
                dataCy="payment-usage-step-continue"
              >
                {t("olkypass.general.continue")}
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
